import { PlatformLocation, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params } from '@angular/router';
import {
  ISbEventPayload,
  ISbStories,
  ISbStoriesParams,
  ISbStory,
  ISbStoryData,
  ISbStoryParams,
  StoryblokBridgeV2,
  storyblokInit,
} from '@storyblok/js';
import {
  BehaviorSubject,
  Observable,
  catchError,
  delay,
  firstValueFrom,
  forkJoin,
  from,
  lastValueFrom,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ISbComponentType, ISbLinkURLObject } from 'storyblok-js-client';
import { defaultLanguage, languages } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { extractLocaleFromPath } from '../lib/locale-extract';
import { removeTrailingSlash } from '../lib/remove-trailing-slash';
import { STORYBLOK_CACHE_TAGS, StoryblokStoriesQuery, recordStoryblokListTags } from '../lib/storyblok-cache-tags';
import { PicflowStoryblok } from '../picflow-storyblok';
import { localeToPathPrefix } from '../pipes/translate-link.pipe';
import { AppConfigService } from '../providers/config.service';
import { customRenderText, fixHtml, removeHtml } from '../storyblok/custom-render-text';
import { getTitleBlockPlainText } from '../storyblok/lib/get-title-block-plain-text';
import { mapStoryblokLang } from '../storyblok/lib/lang-map';
import { getRelationships, getUniqueComponentsList } from '../storyblok/storyblok-find-components';
import { StoryblokLink } from '../storyblok/storyblok-types';
import { BrowserService } from './browser.service';
import { LanguageService } from './language.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StoryblokHelperService } from './storyblok-helper.service';
import { StoryblokReplaceService } from './storyblok-replace.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class StoryblokService implements PicflowStoryblok {
  private platformId = inject<string>(PLATFORM_ID);
  private http = inject(HttpClient);
  private browserService = inject(BrowserService);
  private helper = inject(StoryblokHelperService);
  private dataLoader = inject(ObservableDataLoaderService);
  private config = inject(AppConfigService);
  private noSsr = inject<{ noSsr: boolean }>('NO_SSR' as never, { optional: true });
  private route = inject(ActivatedRoute);
  private storyblokReplaceService = inject(StoryblokReplaceService);
  private transferService = inject(TransferStateService);
  private growthBook = inject(GROWTHBOOK);
  private location = inject(PlatformLocation);
  private languageService = inject(LanguageService);
  private cacheTags = inject(STORYBLOK_CACHE_TAGS, { optional: true });
  private publishableKey = 'qit3qOMDswj7KNAg95r4yAtt';
  private readonly linkMap: { [language: string]: Map<string, ISbLinkURLObject> } = {};

  public updates = new BehaviorSubject<{ story: ISbStoryData } | undefined>(undefined);
  public updates$ = this.updates.pipe(shareReplay(1));
  storyblokInstance: StoryblokBridgeV2;

  constructor() {
    storyblokInit();
    if (isPlatformServer(this.platformId)) return;
    if (!isIframe()) return;
    if (this.browserService.isExtension()) return;
    this.initEditor();
  }

  private async initEditor() {
    await loadScript(this.config.storyblokBridgeUrl);
    console.debug('Loading Storyblok JS for preview mode');

    const { StoryblokBridge } = window;

    this.storyblokInstance = new StoryblokBridge({
      preventClicks: true,
    });

    this.storyblokInstance.on(['change', 'published'], () => {
      this.browserService.reload();
    });

    this.storyblokInstance.on(['input'], (e: ISbEventPayload) => {
      this.resolveRelationships(this.route.snapshot)
        .pipe(take(1))
        .subscribe(relationships => {
          this.helper.tempUpdateCache.set('relationships', relationships);
          if (e.story.content.component === 'root') {
            this.updates.next({ story: this.storyblokReplaceService.replaceStoryValues(e.story) });
          } else {
            this.updates.next({ story: e.story });
          }
          // this.loadComponents(e.story);
        });
    });
  }

  private getCacheVersionAndLanguage() {
    return forkJoin([
      this.transferService
        .withStateTransferObservable('storyblokCV', () =>
          this.dataLoader.get('storyblokCV', () =>
            this.http
              .get<{
                cv: string;
              }>(`https://api.storyblok.com/v2/cdn/stories?content_type=GET_CV&token=${this.publishableKey}`)
              .pipe(map(res => res.cv))
          )
        )
        .pipe(take(1)),
      this.languageService.currentLanguage$.pipe(take(1)),
    ]);
  }

  private getStoryUrl(slug: string, params: ISbStoryParams, cv: string) {
    const query = new URLSearchParams();
    if (params.language && !slug.startsWith('technical/')) {
      query.set('language', params.language);
    }

    if (params.find_by) {
      query.set('find_by', params.find_by);
    }

    return `https://api.storyblok.com/v2/cdn/stories/${slug}?${query.toString()}&token=${this.publishableKey}&version=${
      params.version || 'published'
    }&cv=${cv}&resolve_links=url`;
  }

  private getStoryObservable(
    slug: string,
    params: ISbStoryParams,
    retryCount = 0
  ): Observable<ISbStory['data'] | false> {
    if (!slug || slug.includes('.')) return of(false); // fixes some localhost issues where the browser tries to fetch js files etc.
    return this.getCacheVersionAndLanguage().pipe(
      switchMap(([cv, language]) => {
        const cacheKey = JSON.stringify({ slug, ...params, language });
        return this.dataLoader.get(cacheKey, () => {
          return this.http.get<ISbStory['data']>(this.getStoryUrl(slug, { ...params, language }, cv)).pipe(
            catchError(err => {
              if (err instanceof HttpErrorResponse) {
                if (err.status === 429) {
                  if (retryCount < 3) {
                    const waitTime = Math.pow(2, retryCount) * 1000;
                    return of(null).pipe(
                      delay(waitTime),
                      switchMap(() => this.getStoryObservable(slug, params, retryCount + 1))
                    );
                  }
                }
              }
              return <Observable<undefined>>of(false);
            }),
            map(res => res || (false as const)),
            tap(resp => {
              if (resp) {
                const lang = mapStoryblokLang(resp.story.lang);
                if (!this.linkMap[lang]) this.linkMap[lang] = new Map<string, ISbLinkURLObject>();
                resp.links.forEach(link => {
                  this.linkMap[lang].set(link.uuid, link as ISbLinkURLObject);
                });
              }
            })
          );
        });
      })
    );
  }

  private getQueryParams(snapshot: ActivatedRouteSnapshot = this.route.snapshot): Params {
    if (Object.keys(snapshot.queryParams).length > 0) {
      return snapshot.queryParams;
    }

    if (this.location?.search) {
      const search = new URLSearchParams(this.location.search);
      const queryParams = Object.fromEntries(search.entries());
      return queryParams;
    }
    return {};
  }

  public getStory(slug: string, route?: ActivatedRouteSnapshot): Observable<ISbStory['data'] | false> {
    if (!slug) return of(false);

    const queryParams = this.getQueryParams(route);
    const version = queryParams['_storyblok'] ? 'draft' : 'published';

    const params: ISbStoryParams = { version };
    return this.getStoryObservable(slug, params).pipe(
      tap(res => {
        if (!res) return;
        if (res.story?.id) this.cacheTags?.addItem(res.story.id);
      }),
      switchMap(res => {
        if (!res) return of(undefined);

        const content = res.story.content;
        if (content.abTest) {
          if (isPlatformServer(this.platformId)) {
            if (this.noSsr) {
              this.noSsr.noSsr = true;
            }
            return of(res);
          }
          this.growthBook.triggerExperiment(content.abTest);
          const featureValue = Object.entries(this.growthBook.getFeatures()).find(([, data]) => {
            return data.rules?.find(rule => rule.name === content.abTest);
          })?.[0];
          const abTestValue = this.growthBook.getFeatureValue(featureValue, '');
          if (abTestValue) return this.getStory(abTestValue, route);
          return of(res);
        }
        return of(res);
      }),
      take(1)
    );
  }

  private buildStoriesUrl(params: ISbStoriesParams, cv: string) {
    const query = new URLSearchParams();
    if (params.language) {
      query.set('language', params.language);
    }
    if (params.content_type) {
      query.set('content_type', params.content_type);
    }
    if (params.starts_with) {
      query.set('starts_with', params.starts_with);
    }
    if (params.with_tag) {
      query.set('with_tag', params.with_tag);
    }
    if (params.page) {
      query.set('page', params.page.toString());
    }
    if (params.per_page) {
      query.set('per_page', params.per_page.toString());
    }
    if (params.excluding_fields) {
      query.set('excluding_fields', params.excluding_fields);
    }
    if (params.by_uuids) {
      query.set('by_uuids', params.by_uuids);
    }
    if (params.excluding_ids) {
      query.set('excluding_ids', params.excluding_ids);
    }
    if (params.sort_by) {
      query.set('sort_by', params.sort_by);
    }

    if (params.filter_query) {
      params.filter_query.forEach(item => {
        query.set(`filter_query[${item.key}][in]`, item.value);
      });
    }

    return `https://api.storyblok.com/v2/cdn/stories?${query.toString()}&token=${this.publishableKey}&version=${
      params.version || 'published'
    }&cv=${cv}&resolve_links=url`;
  }

  private getAllStoriesInternal(url: string, retryCount = 0): Observable<HttpResponse<ISbStories['data']>> {
    return this.http.get<ISbStories['data']>(url, { observe: 'response', transferCache: false }).pipe(
      map(resp => resp),
      catchError(err => {
        if (err.status === 429) {
          if (retryCount < 3) {
            const waitTime = Math.pow(2, retryCount) * 1000;
            return of(null).pipe(
              delay(waitTime),
              switchMap(() => this.getAllStoriesInternal(url, retryCount + 1))
            );
          }
        }
        return throwError(() => err);
      })
    );
  }

  private getAllStories(
    params: ISbStoriesParams,
    page = 1,
    stories = []
  ): Observable<{
    stories: ISbStoryData[];
    total: number;
    links: (
      | ISbStoryData<
          ISbComponentType<string> & {
            [index: string]: unknown;
          }
        >
      | ISbLinkURLObject
    )[];
  }> {
    const resultsPerPage = 25;
    return this.getCacheVersionAndLanguage().pipe(
      switchMap(([cv]) => {
        const url = this.buildStoriesUrl({ per_page: resultsPerPage, page, ...params }, cv);
        return this.transferService
          .withStateTransferObservable(url, () => {
            return this.getAllStoriesInternal(url).pipe(
              map(_resp => {
                const total = Number(_resp.headers.get('total')) || 0;
                const resp = _resp.body;
                return {
                  stories: resp.stories,
                  total,
                  links: resp.links,
                };
              })
            );
          })
          .pipe(
            switchMap(resp => {
              const lang = mapStoryblokLang(resp.stories[0]?.lang || params.language);
              if (!this.linkMap[lang]) this.linkMap[lang] = new Map<string, ISbLinkURLObject>();
              resp.links.forEach(link => {
                this.linkMap[lang].set(link.uuid, link as ISbLinkURLObject);
              });

              stories = [...stories, ...resp.stories];

              if (stories.length < resp.total && !params.per_page) {
                return this.getAllStories(params, page + 1, stories);
              }
              return of({ stories, total: resp.total, links: resp.links });
            })
          );
      })
    );
  }

  public getStories(
    options: StoryblokStoriesQuery,
    route: ActivatedRouteSnapshot = this.route.snapshot
  ): Observable<{ stories: ISbStoryData[]; total: number }> {
    const { cache_tagging, ...storyParams } = options;
    return this.getCacheVersionAndLanguage().pipe(
      take(1),
      switchMap(([, language]) => {
        const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
        const cacheKey = JSON.stringify({ ...storyParams, version, language });

        return this.dataLoader
          .get(cacheKey, () =>
            this.getAllStories({ ...storyParams, language }).pipe(
              catchError(err => {
                console.error('Error loading stories', err);
                return of({ stories: [], total: 0 });
              })
            )
          )
          .pipe(
            tap(resp =>
              recordStoryblokListTags(this.cacheTags, { ...storyParams, cache_tagging }, resp?.stories ?? [])
            ),
            take(1)
          );
      })
    );
  }

  public async resolveRelationshipsFromStory(story: ISbStoryData, route: ActivatedRouteSnapshot = this.route.snapshot) {
    if (!story) return [];
    this.cacheTags?.addItem(story.id);

    let ids = Array.from(new Set(getRelationships(story))).filter(Boolean);
    const allStories = [story];

    while (ids.length) {
      const stories = (
        await lastValueFrom(
          this.getStories(
            { by_uuids: ids.join(','), excluding_fields: 'body,preFooter,topBlur,blurOverlay' }, // excluding page fields because of previews where we anyway only need the meta data
            route
          )
        )
      ).stories;

      ids = [];
      stories.forEach(_story => {
        ids.push(...getRelationships(_story));
      });
      ids = Array.from(new Set(ids)).filter(Boolean);
      allStories.push(...stories);
    }

    return allStories;
  }

  public resolveRelationships(route: ActivatedRouteSnapshot, _path?: string, story?: ISbStory['data']) {
    const path = _path || getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home';
    return this.dataLoader.get(`storyblokRelationships_${path}_${this.languageService.currentLanguage()}`, () => {
      return (story ? of(story) : this.getStory(path, route)).pipe(
        switchMap(story => {
          if (!story) return of([]);
          return from(this.resolveRelationshipsFromStory(story.story, route));
        })
      );
    });
  }

  public getUniqueComponentsList(
    route: ActivatedRouteSnapshot,
    _path?: string,
    story?: ISbStory['data']
  ): Observable<Set<string>> {
    const path = removeTrailingSlash(
      _path || getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home'
    );
    return this.dataLoader.get(`uniqueComponentsList_${path}`, () => {
      return forkJoin([
        story ? of(story) : this.getStory(path, route),
        this.resolveRelationships(route, path, story).pipe(take(1)),
      ]).pipe(
        map(([story, relationships]) => {
          if (!story) return new Set<string>([]);
          return getUniqueComponentsList([story.story.content, ...relationships]);
        })
      );
    });
  }

  public getLinkName(link: StoryblokLink): string | undefined {
    if (!link || link.linktype !== 'story' || !link.id) return undefined;
    const currentLanguage = this.languageService.currentLanguage();
    const linkMapItem = this.linkMap[currentLanguage]?.get(link.id) as {
      name?: string;
      translated_name?: string;
    };
    return linkMapItem?.translated_name || linkMapItem?.name;
  }

  public getStoryTitle(story: ISbStoryData): string {
    if (story.content.metaTitle) return story.content.metaTitle;
    const block = this.helper.getBlocks(story.content.body, ['title'], true).find(b => b?.titleText || b?.title);
    return getTitleBlockPlainText(block) || story.name;
  }

  public getStoryDescription(story: ISbStoryData): string {
    if (story.content.lead !== undefined) return story.content.lead;
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = this.helper.getBlocks(story.content.body, ['text'], true)[0];
    if (!block?.content) {
      return 'The next-generation online gallery and proofing platform to boost creative workflows for freelancers and teams.';
    }

    return removeHtml(fixHtml(customRenderText(block.content)));
  }

  public getUrlLink(link: StoryblokLink) {
    if (!link) return;
    const url = (() => {
      if (!link) return '';
      const currentLanguage = this.languageService.currentLanguage();

      const cachedUrl = link.cached_url // add starting slash if missing (its missing for default language links)
        ? link.cached_url.startsWith('/')
          ? link.cached_url
          : '/' + link.cached_url
        : '';

      if (link.linktype === 'email') {
        return `mailto:${(link.url || link.cached_url).replace(/^mailto:/, '')}`;
      }

      if (link.linktype === 'url') {
        const isPassthrough =
          link.url.includes('://') ||
          link.url.startsWith('mailto:') ||
          link.url.startsWith('tel:') ||
          link.url.startsWith('#');
        if (isPassthrough) return link.url;

        // For templated pages, like compare and convert, we can't use story link types, so instead we assume that all links to these pages should have the language prefix
        const strings = ['compare', 'convert'] as const;
        const shouldAddLanguagePrefix = strings.some(str => {
          if (!cachedUrl.startsWith(`/${str}/`) && cachedUrl !== `/${str}`) return false;
          const langData = languages.find(l => l.lang === currentLanguage);
          if (!langData) return false;
          return langData.programaticPages[str];
        });
        const linkContainsLocale = !!extractLocaleFromPath(cachedUrl.split('/').filter(Boolean));
        if (shouldAddLanguagePrefix && !linkContainsLocale) {
          const lang = currentLanguage;
          if (lang !== defaultLanguage) {
            return `/${lang}${cachedUrl}`;
          }
        }

        return cachedUrl;
      }

      if (link.linktype === 'story' && cachedUrl && link.id) {
        const linkMapForLang = this.linkMap[currentLanguage];
        const linkMapItem = linkMapForLang?.get(link.id);
        if (!linkMapForLang || !linkMapItem) {
          // Untranslated story — strip the language prefix so the link points to the default-language URL
          // instead of a non-existent translated page (e.g. /de/blog/… → /blog/…)
          if (currentLanguage !== defaultLanguage && cachedUrl.startsWith(`/${currentLanguage}/`)) {
            return cachedUrl.replace(`/${currentLanguage}/`, '/');
          }
          return cachedUrl;
        }

        if (linkMapItem['translated_name'] !== undefined) {
          // only links that have a translated name (using the translated paths feature in storyblok)
          // are respected. Other links fall back to the default language
          const prefix = localeToPathPrefix(this.languageService.currentLocale());
          const linkPrefix = linkMapItem.full_slug.split('/')[0];
          // We need to remove the prefix and add our own, because the full_slug always contains the original language prefix
          // and we need to replace it with the current locale prefix (which could include location as well)
          const linkWithoutPrefix = linkMapItem.full_slug.replace(`${linkPrefix}/`, '');
          return `/${prefix}/${linkWithoutPrefix}`;
        }

        return `/${linkMapItem.url}`;
      }
      return '';
    })()
      .replace(/\/home$/, '/') // Replacing /home with /
      .replace(/(.+)\/$/, '$1'); // Remove trailing slash

    if (link.anchor) return url + '#' + link.anchor;
    return url;
  }

  public async prepLoadComponents(story: ISbStoryData) {
    if (!story) return [];
    return Array.from(
      await firstValueFrom(
        this.getUniqueComponentsList(this.route.snapshot, story.full_slug, <ISbStory['data']>{ story })
      )
    );
  }
}
